import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import toast from 'react-hot-toast'
import { MarketplaceOffer, OpenChoreWithBids, MyOpenChoreBid } from '@/types'

interface Assignment {
  id: string
  chore_id: string
  assigned_to: string
  status: string
  points_offered: number
  due_date: string
  created_at: string
  chore: {
    id: string
    name: string
    description: string
    category: string
    base_points: number
    estimated_minutes: number
    difficulty: string
    requires_verification: boolean
    icon: string
  }
}

interface TradeOffer {
  id: string
  from_user_id: string
  to_user_id: string
  from_assignment_id: string
  to_assignment_id: string
  points_difference: number
  status: string
  message: string
  created_at: string
  expires_at: string
  from_user: {
    username: string
    display_name: string
    color_theme: string
  }
  to_user: {
    username: string
    display_name: string
    color_theme: string
  }
  from_assignment: Assignment
  to_assignment: Assignment
}

const difficultyColors = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800', 
  hard: 'bg-red-100 text-red-800',
  expert: 'bg-purple-100 text-purple-800'
}

export default function TradingInterface() {
  const [activeTab, setActiveTab] = useState<'trade' | 'offers' | 'bids' | 'marketplace' | 'openChores'>('trade')
  const [myAssignments, setMyAssignments] = useState<Assignment[]>([])
  const [availableForTrade, setAvailableForTrade] = useState<Assignment[]>([])
  const [tradeOffers, setTradeOffers] = useState<TradeOffer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedMyChore, setSelectedMyChore] = useState<string>('')
  const [selectedTargetChore, setSelectedTargetChore] = useState<string>('')
  const [tradeMessage, setTradeMessage] = useState('')
  const { user } = useAuth()
  const { isFeatureEnabled } = useFamilyFeatures()
  const isBiddingEnabled = isFeatureEnabled('chore_bidding')

  // Marketplace state
  const [marketplaceOffers, setMarketplaceOffers] = useState<MarketplaceOffer[]>([])
  const [myMarketplaceOffers, setMyMarketplaceOffers] = useState<MarketplaceOffer[]>([])
  const [marketplaceAssignments, setMarketplaceAssignments] = useState<Assignment[]>([])
  const [createOfferAssignmentId, setCreateOfferAssignmentId] = useState<string>('')

  // Open Chore Bidding state (reverse auction)
  const [openChores, setOpenChores] = useState<OpenChoreWithBids[]>([])
  const [myOpenChoreBids, setMyOpenChoreBids] = useState<MyOpenChoreBid[]>([])
  const [selectedOpenChore, setSelectedOpenChore] = useState<OpenChoreWithBids | null>(null)
  const [openChoreBidAmount, setOpenChoreBidAmount] = useState<string>('')
  const [showBidModal, setShowBidModal] = useState(false)

  useEffect(() => {
    // Check for taskId and tab in URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const taskId = urlParams.get('taskId')
    const tab = urlParams.get('tab')
    
    if (taskId) {
      setSelectedMyChore(taskId)
    }
    
    if (tab === 'bid') {
      setActiveTab('bid')
    }
    
    loadTradingData()
  }, [])

  const loadTradingData = async () => {
    setIsLoading(true)
    try {
      // Load my assignments that can be traded
      try {
        const myChores = await apiClient.getMyAssignments()
        setMyAssignments(myChores.filter((a: Assignment) => a.status === 'claimed' || a.status === 'pending'))
      } catch (error) {
        console.error('Failed to load my assignments:', error)
        setMyAssignments([])
      }

      // Load assignments available for trading from other users
      try {
        const available = await apiClient.getAvailableForTrade()
        setAvailableForTrade(available)
      } catch (error) {
        console.error('Failed to load available trades:', error)
        setAvailableForTrade([])
      }

      // Load trade offers (sent/received)
      try {
        const offers = await apiClient.getTradeOffers()
        setTradeOffers(offers)
      } catch (error) {
        console.error('Failed to load trade offers:', error)
        setTradeOffers([])
      }

      // Load marketplace offers
      try {
        const offers = await apiClient.getMarketplaceOffers()
        setMarketplaceOffers(offers)
      } catch (error) {
        console.error('Failed to load marketplace offers:', error)
        setMarketplaceOffers([])
      }

      // Load my marketplace offers
      try {
        const myOffers = await apiClient.getMyMarketplaceOffers()
        setMyMarketplaceOffers(myOffers)
      } catch (error) {
        console.error('Failed to load my marketplace offers:', error)
        setMyMarketplaceOffers([])
      }

      // Load assignments available for marketplace listing
      try {
        const assignments = await apiClient.getMarketplaceAssignments()
        setMarketplaceAssignments(assignments)
      } catch (error) {
        console.error('Failed to load marketplace assignments:', error)
        setMarketplaceAssignments([])
      }

      // Load open chores for bidding (reverse auction)
      try {
        const chores = await apiClient.getOpenChoresForBidding()
        setOpenChores(chores)
      } catch (error) {
        console.error('Failed to load open chores:', error)
        setOpenChores([])
      }

      // Load my open chore bids
      try {
        const bids = await apiClient.getMyOpenChoreBids()
        setMyOpenChoreBids(bids)
      } catch (error) {
        console.error('Failed to load my open chore bids:', error)
        setMyOpenChoreBids([])
      }
    } catch (error: any) {
      console.error('Critical error loading trading data:', error)
      toast.error('Failed to load some trading data')
    }
    setIsLoading(false)
  }

  const handleCreateTrade = async () => {
    if (!selectedTargetChore) {
      toast.error('Please select a chore you want')
      return
    }

    try {
      await apiClient.createTradeOffer({
        from_assignment_id: selectedMyChore || null,
        to_assignment_id: selectedTargetChore,
        message: tradeMessage
      })

      toast.success(selectedMyChore ? 'Trade offer sent!' : 'Chore request sent!')
      setSelectedMyChore('')
      setSelectedTargetChore('')
      setTradeMessage('')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create trade offer')
    }
  }

  const handleAcceptTrade = async (offerId: string) => {
    try {
      await apiClient.acceptTradeOffer(offerId)
      toast.success('Trade accepted! Chores have been swapped!')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to accept trade')
    }
  }

  const handleRejectTrade = async (offerId: string) => {
    try {
      await apiClient.rejectTradeOffer(offerId)
      toast.success('Trade offer rejected')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject trade')
    }
  }

  // Marketplace handlers
  const handleCreateMarketplaceOffer = async (assignmentId: string) => {
    const assignment = marketplaceAssignments.find(a => a.id === assignmentId)
    if (!assignment) return

    try {
      await apiClient.createMarketplaceOffer({
        assignment_id: assignmentId,
        asking_points: assignment.points_offered
      })
      toast.success('Chore listed in marketplace!')
      setCreateOfferAssignmentId('')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create offer')
    }
  }

  const handleAcceptMarketplaceOffer = async (offerId: string) => {
    try {
      await apiClient.acceptMarketplaceOffer(offerId)
      toast.success('Chore is now yours!')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to accept offer')
    }
  }

  const handleCancelMarketplaceOffer = async (offerId: string) => {
    try {
      await apiClient.cancelMarketplaceOffer(offerId)
      toast.success('Offer cancelled')
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to cancel offer')
    }
  }

  // Open Chore Bidding handlers (reverse auction)
  const handlePlaceOpenChoreBid = async () => {
    if (!selectedOpenChore) return
    const bidPoints = parseInt(openChoreBidAmount)

    if (isNaN(bidPoints) || bidPoints < 1) {
      toast.error('Please enter a valid bid amount (minimum 1 point)')
      return
    }

    const maxBid = selectedOpenChore.lowest_bid || selectedOpenChore.base_points
    if (bidPoints >= maxBid) {
      toast.error(`Bid must be lower than ${maxBid} points`)
      return
    }

    try {
      await apiClient.placeOpenChoreBid(selectedOpenChore.id, bidPoints)
      toast.success("You're now the lowest bidder!")
      setSelectedOpenChore(null)
      setOpenChoreBidAmount('')
      setShowBidModal(false)
      loadTradingData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Bid must be lower than current lowest')
    }
  }

  const formatTimeLeft = (expiresAt: string) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffMs = expires.getTime() - now.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    
    if (diffMs <= 0) return 'Expired'
    if (diffHours > 0) return `${diffHours}h ${diffMins}m left`
    return `${diffMins}m left`
  }

  const ChoreCard = ({ assignment, isSelectable = false, onSelect, isSelected = false }: {
    assignment: any  // Changed to any to handle incomplete data
    isSelectable?: boolean
    onSelect?: () => void
    isSelected?: boolean
  }) => {
    // Handle cases where chore data might be missing or structured differently
    const choreName = assignment?.chore?.name || assignment?.chore_name || 'Unknown Chore'
    const choreDescription = assignment?.chore?.description || assignment?.description || ''
    const difficulty = assignment?.chore?.difficulty || assignment?.difficulty || 'medium'
    const estimatedMinutes = assignment?.chore?.estimated_minutes || assignment?.estimated_minutes || 15
    const pointsOffered = assignment?.points_offered || assignment?.chore?.base_points || 10
    const category = assignment?.chore?.category || assignment?.category || 'routine'
    
    // Format the due date
    const formatDueDate = (dateStr: string) => {
      if (!dateStr) return ''
      const date = new Date(dateStr.includes?.('T') ? dateStr : dateStr + 'T12:00:00')
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today'
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow'
      } else {
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      }
    }
    
    const dueDate = formatDueDate(assignment?.due_date)
    
    return (
      <div 
        className={`card border-2 cursor-pointer transition-all ${
          isSelected 
            ? 'border-blue-500 bg-blue-50' 
            : isSelectable 
              ? 'border-gray-200 hover:border-blue-300' 
              : 'border-gray-200'
        }`}
        onClick={onSelect}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <h4 className="font-semibold text-gray-900">{choreName}</h4>
            {choreDescription && (
              <p className="text-sm text-gray-600">{choreDescription}</p>
            )}
            {dueDate && (
              <p className="text-xs text-gray-500 mt-1">Due: {dueDate}</p>
            )}
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-blue-600">
              {pointsOffered}
            </div>
            <div className="text-xs text-gray-500">points</div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficultyColors[difficulty as keyof typeof difficultyColors] || difficultyColors.medium}`}>
            {difficulty}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
            ~{estimatedMinutes} min
          </span>
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
            {category}
          </span>
        </div>
      </div>
    )
  }

  // Guard: if chore_bidding feature is disabled, show message
  if (!isBiddingEnabled && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="text-4xl mb-4">🏪</div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Marketplace Not Available</h2>
        <p className="text-gray-600">
          The chore trading feature is not enabled for your family.
          Ask a parent to enable it in Family Settings.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading trading data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('trade')}
          className={`py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
            activeTab === 'trade'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Trade
        </button>
        <button
          onClick={() => setActiveTab('marketplace')}
          className={`py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
            activeTab === 'marketplace'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Market
        </button>
        <button
          onClick={() => setActiveTab('openChores')}
          className={`py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
            activeTab === 'openChores'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Open
        </button>
        <button
          onClick={() => setActiveTab('offers')}
          className={`py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
            activeTab === 'offers'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Offers ({tradeOffers.length})
        </button>
        <button
          onClick={() => setActiveTab('bids')}
          className={`py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
            activeTab === 'bids'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Bids ({myOpenChoreBids.length})
        </button>
      </div>

      {/* Trade Chores Tab */}
      {activeTab === 'trade' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              🤝 Trade Chores with Siblings
              <span className="ml-2 text-sm font-normal text-gray-600">
                Swap chores or request ones you want!
              </span>
            </h2>
            
            {availableForTrade.length === 0 ? (
              /* When no siblings have chores to trade, show guidance */
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm mb-3">
                  No siblings have chores available for direct trading right now. Try these alternatives:
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveTab('marketplace')}
                    className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md text-sm font-medium hover:bg-purple-200"
                  >
                    List in Marketplace
                  </button>
                  <button
                    onClick={() => setActiveTab('openChores')}
                    className="px-3 py-1.5 bg-green-100 text-green-700 rounded-md text-sm font-medium hover:bg-green-200"
                  >
                    Bid on Open Chores
                  </button>
                </div>

                {myAssignments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <h3 className="font-semibold text-gray-900 mb-3">Your Chores ({myAssignments.length})</h3>
                    <div className="space-y-2">
                      {myAssignments.map((assignment) => (
                        <ChoreCard
                          key={assignment.id}
                          assignment={assignment}
                          isSelectable={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Normal two-column layout when siblings have chores */
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* My Chores */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Your Chores to Trade</h3>
                    {myAssignments.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        <p>You have no chores to offer</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {myAssignments.map((assignment) => (
                          <ChoreCard
                            key={assignment.id}
                            assignment={assignment}
                            isSelectable={true}
                            onSelect={() => setSelectedMyChore(assignment.id)}
                            isSelected={selectedMyChore === assignment.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Available for Trade */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Available from Siblings</h3>
                    <div className="space-y-3">
                      {availableForTrade.map((assignment) => (
                        <ChoreCard
                          key={assignment.id}
                          assignment={assignment}
                          isSelectable={true}
                          onSelect={() => setSelectedTargetChore(assignment.id)}
                          isSelected={selectedTargetChore === assignment.id}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Trade Form */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trade Message (Optional)
                    </label>
                    <textarea
                      value={tradeMessage}
                      onChange={(e) => setTradeMessage(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md resize-none"
                      rows={2}
                      placeholder="Hey! Want to swap chores? I think this would work better for both of us..."
                    />
                  </div>

                  <button
                    onClick={handleCreateTrade}
                    disabled={!selectedTargetChore}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {selectedMyChore ? 'Create Trade Offer' : 'Request Chore'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* Trade Offers Tab */}
      {activeTab === 'offers' && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            📬 Trade Offers
            <span className="ml-2 text-sm font-normal text-gray-600">
              Offers you've sent and received
            </span>
          </h2>
          
          {tradeOffers.length === 0 ? (
            <div className="card text-center py-8">
              <div className="text-4xl mb-2">📬</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No trade offers yet
              </h3>
              <p className="text-gray-600">
                Start by creating a trade offer in the "Trade Chores" tab!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tradeOffers.map((offer) => (
                <div key={offer.id} className="card border-2 border-blue-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: offer.from_user?.color_theme || '#6B7280' }}
                        ></div>
                        <span className="font-semibold text-gray-900">
                          {offer.from_user?.display_name || 'Unknown'}
                        </span>
                        <span className="text-gray-600">wants to trade</span>
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: offer.to_user?.color_theme || '#6B7280' }}
                        ></div>
                        <span className="font-semibold text-gray-900">
                          {offer.to_user?.display_name || 'Unknown'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatTimeLeft(offer.expires_at)} • {offer.status}
                      </div>
                    </div>
                    
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      offer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      offer.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {offer.status}
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Offering:</h4>
                      <ChoreCard assignment={offer.from_assignment} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">For:</h4>
                      <ChoreCard assignment={offer.to_assignment} />
                    </div>
                  </div>
                  
                  {offer.message && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-700">"{offer.message}"</div>
                    </div>
                  )}
                  
                  {offer.status === 'pending' && offer.to_user_id === user?.id && (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleAcceptTrade(offer.id)}
                        className="btn-primary flex-1"
                      >
                        Accept Trade ✅
                      </button>
                      <button
                        onClick={() => handleRejectTrade(offer.id)}
                        className="btn-secondary flex-1"
                      >
                        Decline ❌
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Bids Tab - Shows open chore bids (reverse auction) */}
      {activeTab === 'bids' && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            🎯 My Bids
            <span className="ml-2 text-sm font-normal text-gray-600">
              Track your reverse auction bids on open chores
            </span>
          </h2>

          {myOpenChoreBids.length === 0 ? (
            <div className="card text-center py-8">
              <div className="text-4xl mb-2">🎯</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No bids placed yet
              </h3>
              <p className="text-gray-600">
                Go to the Open tab to bid on unassigned chores!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {myOpenChoreBids.map((bid) => (
                <div
                  key={bid.id}
                  className={`card border-2 ${
                    bid.is_winning
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{bid.chore_name}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm">
                          Your bid: <span className="font-bold text-green-600">{bid.bid_points} pts</span>
                        </span>
                        <span className="text-sm text-gray-500">
                          Base: {bid.base_points} pts
                        </span>
                        {bid.lowest_bid && bid.lowest_bid !== bid.bid_points && (
                          <span className="text-sm text-orange-600">
                            Lowest: {bid.lowest_bid} pts
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      {bid.is_winning && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Winning!
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        bid.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                        bid.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {bid.status}
                      </span>
                    </div>
                  </div>

                  {!bid.is_winning && bid.status === 'active' && (
                    <p className="text-sm text-orange-600 mt-2">
                      Someone bid lower! Go to the Open tab to place a lower bid.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marketplace Tab */}
      {activeTab === 'marketplace' && (
        <div className="space-y-6">
          {/* Explanation Card */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-2">Chore Hand-offs</h3>
            <p className="text-sm text-purple-700">
              When a sibling can't or doesn't want to do their chore, they can list it here for someone else to take over.
              You'll earn the points when you complete the chore!
            </p>
          </div>

          {/* Available Offers Section */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Available Hand-offs</h3>
            <p className="text-sm text-gray-600 mb-4">
              These chores were listed by siblings who want someone else to take over. Take one and earn the points!
            </p>

            {marketplaceOffers.filter(o => o.offered_by !== user?.id).length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <div className="text-3xl mb-2">🛒</div>
                <p>No chores available in the marketplace</p>
              </div>
            ) : (
              <div className="space-y-3">
                {marketplaceOffers
                  .filter(o => o.offered_by !== user?.id)
                  .map(offer => (
                    <div key={offer.id} className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{offer.assignment?.chore?.name || 'Chore'}</h4>
                          <p className="text-sm text-gray-600">
                            {offer.assignment?.points_offered || offer.asking_points} points
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            From {offer.offered_by_user?.display_name || 'Family member'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleAcceptMarketplaceOffer(offer.id)}
                          className="btn-primary text-sm"
                        >
                          Take Over
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* My Listings Section */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Your Listings</h3>

            {myMarketplaceOffers.length === 0 ? (
              <p className="text-gray-500">You have no active listings</p>
            ) : (
              <div className="space-y-3">
                {myMarketplaceOffers.map(offer => (
                  <div key={offer.id} className="p-4 border border-purple-300 bg-purple-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold">{offer.assignment?.chore?.name || 'Chore'}</h4>
                        <p className="text-sm text-gray-600">
                          {offer.assignment?.points_offered || offer.asking_points} points
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelMarketplaceOffer(offer.id)}
                        className="text-red-600 text-sm hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create New Listing Section */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">List a Chore</h3>

            {marketplaceAssignments.length === 0 ? (
              <p className="text-gray-500">No chores available to list</p>
            ) : (
              <div className="space-y-4">
                <select
                  value={createOfferAssignmentId}
                  onChange={(e) => setCreateOfferAssignmentId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select a chore to list...</option>
                  {marketplaceAssignments.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.chore?.name || 'Chore'} ({a.points_offered} pts)
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => createOfferAssignmentId && handleCreateMarketplaceOffer(createOfferAssignmentId)}
                  disabled={!createOfferAssignmentId}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  List in Marketplace
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Open Chores Tab (Reverse Auction) */}
      {activeTab === 'openChores' && (
        <div className="space-y-6">
          {/* Explanation Card */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">Reverse Auction</h3>
            <p className="text-sm text-green-700">
              Bid DOWN to do these chores! The lowest bidder wins the chore at their bid price.
              For example, if a chore is worth 20 points and you bid 15, you'll earn 15 points if you win.
            </p>
          </div>

          {/* Open Chores List */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Open Chores</h3>

            {openChores.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <div className="text-3xl mb-2">📋</div>
                <p>No open chores available for bidding</p>
              </div>
            ) : (
              <div className="space-y-4">
                {openChores.map(chore => (
                  <div key={chore.id} className="p-4 border border-green-200 rounded-lg bg-green-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold">{chore.chore_name}</h4>
                        {chore.chore_description && (
                          <p className="text-sm text-gray-600">{chore.chore_description}</p>
                        )}
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="text-green-700">Base: {chore.base_points} pts</span>
                          {chore.lowest_bid && (
                            <span className="text-orange-600">Lowest bid: {chore.lowest_bid} pts</span>
                          )}
                          <span className="text-gray-500">{chore.bid_count} bids</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedOpenChore(chore)
                          setOpenChoreBidAmount(
                            String((chore.lowest_bid || chore.base_points) - 1)
                          )
                          setShowBidModal(true)
                        }}
                        className="btn-primary text-sm"
                      >
                        Bid Lower
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* My Open Chore Bids */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Your Bids</h3>

            {myOpenChoreBids.length === 0 ? (
              <p className="text-gray-500">You haven't placed any bids on open chores</p>
            ) : (
              <div className="space-y-3">
                {myOpenChoreBids.map(bid => (
                  <div
                    key={bid.id}
                    className={`p-4 border rounded-lg ${
                      bid.is_winning
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{bid.chore_name}</h4>
                        <p className="text-sm">
                          Your bid: <span className="font-medium">{bid.bid_points} pts</span>
                          {bid.is_winning && (
                            <span className="text-green-600 ml-2">(Winning!)</span>
                          )}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs ${
                        bid.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                        bid.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {bid.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Open Chore Bid Modal */}
      {showBidModal && selectedOpenChore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Place Your Bid</h3>

            <div className="mb-4">
              <p className="font-medium">{selectedOpenChore.chore_name}</p>
              <p className="text-sm text-gray-600">Base value: {selectedOpenChore.base_points} pts</p>
              {selectedOpenChore.lowest_bid && (
                <p className="text-sm text-orange-600">
                  Current lowest: {selectedOpenChore.lowest_bid} pts
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Your Bid (points)</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setOpenChoreBidAmount(String(Math.max(1, parseInt(openChoreBidAmount) - 1)))}
                  className="w-10 h-10 rounded-full bg-gray-100 text-xl hover:bg-gray-200"
                >
                  -
                </button>
                <input
                  type="number"
                  value={openChoreBidAmount}
                  onChange={(e) => setOpenChoreBidAmount(e.target.value)}
                  className="w-24 text-center text-2xl font-bold border-2 rounded-lg p-2"
                  min="1"
                />
                <button
                  onClick={() => setOpenChoreBidAmount(String(parseInt(openChoreBidAmount || '0') + 1))}
                  className="w-10 h-10 rounded-full bg-gray-100 text-xl hover:bg-gray-200"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Must be lower than {selectedOpenChore.lowest_bid || selectedOpenChore.base_points} pts
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBidModal(false)
                  setSelectedOpenChore(null)
                }}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handlePlaceOpenChoreBid}
                disabled={
                  parseInt(openChoreBidAmount) >= (selectedOpenChore.lowest_bid || selectedOpenChore.base_points) ||
                  parseInt(openChoreBidAmount) < 1 ||
                  isNaN(parseInt(openChoreBidAmount))
                }
                className="flex-1 btn-primary disabled:opacity-50"
              >
                Place Bid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}